import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { MarketplaceActivity } from 'types/marketplace-activity';
import { getPublisherActivity } from '../api/marketplaceApi';

export interface UsePublisherActivityResult {
  activity: MarketplaceActivity[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePublisherActivity(
  slug: string | null,
  opts?: { limit?: number; offset?: number }
): UsePublisherActivityResult {
  const apiClients = useApiClientsOrNull();

  const [activity, setActivity] = useState<MarketplaceActivity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => setFetchTrigger((prev) => prev + 1), []);

  useEffect(() => {
    if (!apiClients || !slug) {
      setActivity([]);
      setTotal(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getPublisherActivity(apiClients.schemaClient, slug, opts)
      .then((res) => {
        if (!cancelled) {
          setActivity(res.activity);
          setTotal(res.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load activity');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiClients, slug, fetchTrigger, opts?.limit, opts?.offset]);

  return { activity, total, loading, error, refetch };
}
